import React from 'react'
import {
  List,
  Datagrid,
  TextField,
  DateField,
  EditButton,
  DeleteButton,
  TopToolbar,
  CreateButton,
  ExportButton,
  ReferenceField,
  FunctionField,
  useTranslate,
  useDataProvider,
  useNotify,
  useListContext,
  BulkDeleteButton,
  useRefresh,
  useGetList,
} from 'react-admin'
import {
  DocumentIcon,
  ArrowPathIcon,
  InboxIcon,
} from '@heroicons/react/24/outline'
import {
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
} from '@mui/material'

type VendorSummaryRow = {
  vendor_id: number
  vendor_name: string
  total_contracts: number
  summary_status: string
  completed_count: number
  review_required_count: number
}

type DocumentTypeSummaryRow = {
  extracted_document_type: string
  total_contracts: number
  summary_status: string
  completed_count: number
  review_required_count: number
}

const ContractEmpty = ({ tab }) => {
  const { filterValues } = useListContext()
  const isVerifiedTab = tab === 'verified'
  const isReviewTab = tab === 'review'
  const isFailedTab = tab === 'failed'

  if (isVerifiedTab) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
        <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          No Verified Contracts
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          Check the &quot;Needs Review&quot; tab to verify and approve newly
          uploaded documents.
        </Typography>
      </Box>
    )
  }

  if (isReviewTab) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
        <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          No Contracts Need Review
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          All contracts have been processed or are already verified.
        </Typography>
      </Box>
    )
  }

  if (isFailedTab) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
        <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          No Failed Contracts
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          Contracts that fail extraction will appear here for reprocessing.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
      <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
      <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
        No Contract Repository yet.
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>
        Do you want to add one?
      </Typography>
      <CreateButton label="Create" variant="contained" />
    </Box>
  )
}

const ContractStatusChip = ({ record }) => {
  if (!record) return null

  const colors = {
    uploaded: 'default',
    processing: 'primary',
    review_required: 'warning',
    completed: 'success',
    failed: 'error',
    extraction_failed: 'error',
  }

  const labelMap = {
    uploaded: 'UPLOADED',
    processing: 'PROCESSING',
    review_required: 'REVIEW REQUIRED',
    completed: 'VERIFIED',
    failed: 'FAILED',
    extraction_failed: 'EXTRACTION FAILED',
  }
  const label = labelMap[record.status] || 'UNKNOWN'

  return (
    <Chip
      label={label}
      color={colors[record.status] || 'default'}
      size="small"
      variant="filled"
    />
  )
}

const RetryButton = ({ record }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [loading, setLoading] = React.useState(false)
  const metadataCount = Array.isArray(record?.metadata_fields)
    ? record.metadata_fields.length
    : 0
  const terminalNoDataFailure =
    typeof record?.extraction_error_message === 'string' &&
    (record.extraction_error_message.includes(
      'No usable metadata values extracted from document'
    ) ||
      record.extraction_error_message.includes(
        'Unsupported PDF portfolio format'
      ))
  const canRetry =
    record &&
    (record.status === 'extraction_failed' ||
      (record.status === 'failed' && !terminalNoDataFailure) ||
      (record.status === 'review_required' && metadataCount === 0))

  if (!canRetry) return null

  const handleRetry = (e) => {
    e.stopPropagation() // Prevent row click
    setLoading(true)
    dataProvider
      .retryExtraction(record.id)
      .then(() => {
        notify('Extraction retried successfully', { type: 'info' })
        refresh()
      })
      .catch((error) => {
        notify(`Error retrying extraction: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Tooltip
      title={
        record.status === 'review_required'
          ? 'Retry extraction (no fields extracted)'
          : 'Retry AI Extraction'
      }
    >
      <IconButton
        onClick={handleRetry}
        disabled={loading}
        size="small"
        sx={{
          color: record.status === 'review_required' ? '#2563eb' : '#ef4444',
        }}
      >
        <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
      </IconButton>
    </Tooltip>
  )
}

const UNIVERSAL_FIELDS = [
  'Supplier Name',
  'Signatory Details',
  'Contract Type',
  'Categorisation',
  'Linked Contracts',
  'Parent Agreement',
  'Referenced Schedules',
  'Effective Date',
  'Expiration Date',
  'Auto-Renewal Clause',
  'Notice Period',
  'Payment Terms',
  'Contract Value',
  'Liability Cap',
  'Indemnification',
  'Governing Law',
  'Dispute Resolution',
  'Termination Rights',
  'Change of Control',
  'Data Privacy',
  'Confidentiality',
  'Force Majeure',
  'Insurance',
  'Assignment Rights',
]

const isFieldMandatory = (fieldName) => {
  if (!fieldName) return false
  const normalizedFieldName = fieldName.toLowerCase()
  return UNIVERSAL_FIELDS.some(
    (uf) =>
      normalizedFieldName.includes(uf.toLowerCase()) ||
      uf.toLowerCase().includes(normalizedFieldName)
  )
}

const ConfidenceIndicator = ({ record }) => {
  if (!record) return null

  if (typeof record.ai_confidence === 'number') {
    const confidence = Math.round(record.ai_confidence * 100)
    let color = 'bg-red-500'
    if (confidence >= 80) color = 'bg-green-500'
    else if (confidence >= 50) color = 'bg-amber-500'

    return (
      <div className="flex items-center space-x-2">
        <div className={`w-3 h-3 rounded-full ${color}`} />
        <span className="text-xs text-stone-500">{confidence}%</span>
      </div>
    )
  }

  if (!record.metadata_fields || record.metadata_fields.length === 0)
    return null

  // Calculate average confidence for fields.
  // We include mandatory fields in the average even if they are empty (treated as 0%).
  // Optional fields that were not found (value is null, empty or 'None') are excluded.
  const fieldsForConfidence = record.metadata_fields.filter((f) => {
    const isMandatory = isFieldMandatory(f.field_name)
    const hasValue =
      f.field_value &&
      f.field_value !== 'None' &&
      f.field_value !== 'null' &&
      f.field_value.trim() !== ''
    return isMandatory || hasValue
  })

  if (fieldsForConfidence.length === 0) return null

  const sumScores = fieldsForConfidence.reduce((a, b) => {
    const hasValue =
      b.field_value &&
      b.field_value !== 'None' &&
      b.field_value !== 'null' &&
      b.field_value.trim() !== ''
    return a + (hasValue ? b.confidence_score || 0 : 0)
  }, 0)

  const avgScore = sumScores / fieldsForConfidence.length
  const confidence = Math.round(avgScore * 100)

  let color = 'bg-red-500'
  if (confidence >= 80) color = 'bg-green-500'
  else if (confidence >= 50) color = 'bg-amber-500'

  return (
    <div className="flex items-center space-x-2">
      <div className={`w-3 h-3 rounded-full ${color}`} />
      <span className="text-xs text-stone-500">{confidence}%</span>
    </div>
  )
}

const ContractListActions = () => {
  const { selectedIds } = useListContext()
  const selectionTrue = selectedIds && selectedIds.length > 0
  const translate = useTranslate()

  const confirmContent = `Are you sure you want to delete ${selectedIds?.length} selected contracts? This action cannot be undone.`

  return (
    <TopToolbar>
      <CreateButton
        label="Upload Contract"
        variant="contained"
        sx={{ marginRight: 2 }}
      />
      <BulkDeleteButton
        label="Delete Selected"
        disabled={!selectionTrue}
        mutationMode="pessimistic"
        confirmTitle="Confirm Bulk Deletion"
        confirmContent={confirmContent}
        sx={{
          marginRight: 2,
          color: selectionTrue ? 'white' : 'inherit',
          backgroundColor: selectionTrue ? '#ef4444' : 'transparent',
          '&:hover': {
            backgroundColor: selectionTrue ? '#dc2626' : 'transparent',
          },
          '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' },
        }}
      />
      <ExportButton />
    </TopToolbar>
  )
}

const isProcessing = (status) => ['uploaded', 'processing'].includes(status)

const PollingComponent = () => {
  const { refetch, isFetching } = useListContext()
  const dataProvider = useDataProvider()
  const [hasProcessingItems, setHasProcessingItems] = React.useState(false)
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const isFetchingRef = React.useRef(isFetching)

  // Keep isFetchingRef in sync
  React.useEffect(() => {
    isFetchingRef.current = isFetching
  }, [isFetching])

  const checkProcessing = React.useCallback(() => {
    // Only check if we are not already fetching the main list
    if (isFetchingRef.current) return

    dataProvider
      .getList('contracts', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'DESC' },
        filter: { status: ['uploaded', 'processing'] },
      })
      .then((response) => {
        const count = response.total || 0
        const processing = count > 0
        setHasProcessingItems(processing)

        if (processing) {
          refetch()
        }
      })
      .catch(() => {
        // Keep polling alive on transient errors.
      })
  }, [dataProvider, refetch])

  React.useEffect(() => {
    // Initial check
    checkProcessing()

    // Setup polling every 5 seconds
    timerRef.current = setInterval(checkProcessing, 5000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [checkProcessing])

  // One final refetch when we transition from hasProcessingItems=true to false
  const prevHasProcessingItem = React.useRef(false)
  React.useEffect(() => {
    if (prevHasProcessingItem.current && !hasProcessingItems) {
      refetch()
    }
    prevHasProcessingItem.current = hasProcessingItems
  }, [hasProcessingItems, refetch])

  return null
}

const VendorContractsTable = ({
  onVendorClick,
}: {
  onVendorClick: (vendor: VendorSummaryRow) => void
}) => {
  const { data, isLoading, error } = useGetList('contracts/by-vendor', {
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'total_contracts', order: 'DESC' },
    filter: {},
  })
  const rows = (data || []) as VendorSummaryRow[]

  const statusChip = (status) => {
    if (status === 'verified')
      return <Chip size="small" label="Verified" color="success" />
    if (status === 'review_required')
      return <Chip size="small" label="Needs Review" color="warning" />
    return <Chip size="small" label="In Progress" color="default" />
  }

  if (isLoading) {
    return <Typography sx={{ p: 2 }}>Loading vendors...</Typography>
  }

  if (error) {
    return (
      <Typography sx={{ p: 2 }} color="error">
        Could not load vendors.
      </Typography>
    )
  }

  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Vendor</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Contracts</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.vendor_id} hover>
              <TableCell>
                <Button
                  variant="text"
                  onClick={() => onVendorClick(row)}
                  sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                >
                  {row.vendor_name}
                </Button>
              </TableCell>
              <TableCell>{statusChip(row.summary_status)}</TableCell>
              <TableCell align="right">{row.total_contracts}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography color="text.secondary">
                  No vendors with contracts found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

const DocumentTypeContractsTable = ({
  onTypeClick,
}: {
  onTypeClick: (docType: string) => void
}) => {
  const { data, isLoading, error } = useGetList('contracts/by-document-type', {
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'total_contracts', order: 'DESC' },
    filter: {},
  })
  const rows = (data || []) as DocumentTypeSummaryRow[]

  const statusChip = (status) => {
    if (status === 'verified')
      return <Chip size="small" label="Verified" color="success" />
    if (status === 'review_required')
      return <Chip size="small" label="Needs Review" color="warning" />
    return <Chip size="small" label="In Progress" color="default" />
  }

  if (isLoading) {
    return <Typography sx={{ p: 2 }}>Loading document types...</Typography>
  }

  if (error) {
    return (
      <Typography sx={{ p: 2 }} color="error">
        Could not load document types.
      </Typography>
    )
  }

  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Document Type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Contracts</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.extracted_document_type} hover>
              <TableCell>
                <Button
                  variant="text"
                  onClick={() => onTypeClick(row.extracted_document_type)}
                  sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                >
                  {row.extracted_document_type}
                </Button>
              </TableCell>
              <TableCell>{statusChip(row.summary_status)}</TableCell>
              <TableCell align="right">{row.total_contracts}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography color="text.secondary">
                  No extracted document types found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

export const ContractList = () => {
  const [tab, setTab] = React.useState('all')
  const [selectedVendor, setSelectedVendor] =
    React.useState<VendorSummaryRow | null>(null)
  const [selectedDocumentType, setSelectedDocumentType] = React.useState<
    string | null
  >(null)
  const { total: failedTotal = 0 } = useGetList('contracts', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'DESC' },
    filter: { needs_reprocessing: true },
  })

  const handleTabChange = (event, newValue) => {
    setTab(newValue)
    if (newValue === 'by_vendor') {
      setSelectedVendor(null)
    }
    if (newValue === 'by_document_type') {
      setSelectedDocumentType(null)
    }
  }

  const baseFilter =
    tab === 'all'
      ? {}
      : tab === 'verified'
      ? { is_verified: true }
      : tab === 'failed'
      ? { needs_reprocessing: true }
      : { status: 'review_required' }
  const filter = selectedVendor?.vendor_id
    ? { ...baseFilter, vendor_id: selectedVendor.vendor_id }
    : selectedDocumentType
    ? { ...baseFilter, extracted_document_type: selectedDocumentType }
    : baseFilter

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label="All Contracts" value="all" />
          <Tab label="Needs Review" value="review" />
          <Tab label="Verified" value="verified" />
          <Tab
            label={failedTotal > 0 ? `Failed (${failedTotal})` : 'Failed'}
            value="failed"
          />
          <Tab label="By Vendor" value="by_vendor" />
          <Tab label="By Document Type" value="by_document_type" />
        </Tabs>
      </Box>
      {tab === 'by_vendor' ? (
        <VendorContractsTable
          onVendorClick={(vendorRow) => {
            setSelectedVendor(vendorRow)
            setTab('all')
          }}
        />
      ) : tab === 'by_document_type' ? (
        <DocumentTypeContractsTable
          onTypeClick={(docType) => {
            setSelectedDocumentType(docType)
            setTab('all')
          }}
        />
      ) : (
        <>
          {selectedVendor?.vendor_id && (
            <Box sx={{ mb: 1.5 }}>
              <Chip
                label={`Filtered by vendor: ${selectedVendor.vendor_name}`}
                onDelete={() => setSelectedVendor(null)}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
          {selectedDocumentType && (
            <Box sx={{ mb: 1.5 }}>
              <Chip
                label={`Filtered by document type: ${selectedDocumentType}`}
                onDelete={() => setSelectedDocumentType(null)}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
          <List
            actions={<ContractListActions />}
            filter={filter}
            empty={<ContractEmpty tab={tab} />}
            sort={{ field: 'created_at', order: 'DESC' }}
            sx={{
              '& .RaList-main': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
            }}
          >
            <PollingComponent />
            <Datagrid
              rowClick="edit"
              sx={{
                '& .MuiTableCell-head': {
                  fontWeight: 'bold',
                  backgroundColor: '#f9f8f7',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: '#f1f0ee !important',
                },
              }}
            >
              <TextField source="id" />
              <TextField
                source="filename"
                label="Document Name"
                sx={{ fontWeight: 'medium' }}
              />
              <ReferenceField
                source="vendor_id"
                reference="vendors"
                label="Linked Vendor"
                sortBy="vendor_name"
              >
                <TextField source="name" />
              </ReferenceField>
              <TextField source="contract_type" label="Type" />
              <FunctionField
                label="Status"
                sortBy="status"
                render={(record) => <ContractStatusChip record={record} />}
              />
              <FunctionField
                label="AI Confidence"
                sortBy="ai_confidence"
                render={(record) => <ConfidenceIndicator record={record} />}
              />
              <DateField source="created_at" label="Uploaded" showTime />
              <FunctionField
                label="Actions"
                sortable={false}
                render={(record) => (
                  <div className="flex items-center space-x-1">
                    <RetryButton record={record} />
                    <EditButton />
                    <DeleteButton
                      mutationMode="pessimistic"
                      confirmTitle={`Delete Contract: ${record.filename}`}
                      confirmContent={`Are you sure you want to delete the contract "${record.filename}"? This action cannot be undone.`}
                    />
                  </div>
                )}
              />
            </Datagrid>
          </List>
        </>
      )}
      {/* 
          We place PollingComponent outside the List to ensure it remains mounted
          during tab changes and is not duplicated within the Datagrid or Empty state.
          However, PollingComponent needs access to ListContext for refetch(), 
          so we must wrap it in ListContextProvider if we put it outside.
          Alternatively, we can use useRefresh() inside PollingComponent if we don't
          need the surgical precision of ListContext's refetch().
          Given the user's "hammering" concern, using the surgical refetch() inside 
          List is safer, but we must only have ONE instance.
      */}
    </Box>
  )
}

export const ContractIcon = DocumentIcon
